import React, { createContext, FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Features } from '../../model/features'
import { useReadyFeatureFlag } from '../../state/featureFlags/hooks'
import { useLaunchDarklyIdentityReady } from '../../providers/CheckoutLaunchDarklyContext'
import {
  createOwnershipReady,
  createOwnershipResponse,
  MOBILE_OWNERSHIP_READY_TIMEOUT_MS,
  OwnershipCommit,
  OwnershipOffer,
  parseOwnershipCommand,
} from './mobileOwnershipProtocol'

export type CheckoutJourneyOwner = 'negotiating' | 'rocketship' | 'afterpay-mobile'
export type CheckoutJourneyStartBoundary = Readonly<{
  journeyId?: string
  startedAtEpochMs?: number
}>
export type CheckoutJourneyOwnership = Readonly<{
  owner: CheckoutJourneyOwner
  didFallbackFromMobileNegotiation: boolean
  ownershipExitReady: boolean
  reportingEligibilityResolved: boolean
  reportingEnabled: boolean
  journeyId?: string
  startedAtEpochMs?: number
  claimRocketshipOwnership: () => CheckoutJourneyStartBoundary | undefined
  canStartRocketshipJourney: () => boolean
  isRocketshipOwner: () => boolean
  waitForReportingEligibility: () => Promise<void>
  waitForOwnershipResolution: () => Promise<void>
}>

declare global {
  interface Window {
    __afterpayMobileUserJourneyV1?: boolean
  }
}

const claimUnavailable = (): undefined => undefined
const CheckoutJourneyOwnershipContext = createContext<CheckoutJourneyOwnership>({
  owner: 'rocketship',
  didFallbackFromMobileNegotiation: false,
  ownershipExitReady: true,
  reportingEligibilityResolved: true,
  reportingEnabled: false,
  claimRocketshipOwnership: claimUnavailable,
  canStartRocketshipJourney: () => true,
  isRocketshipOwner: () => true,
  waitForReportingEligibility: () => Promise.resolve(),
  waitForOwnershipResolution: () => Promise.resolve(),
})

const hasMobileOwnershipCapability = (): boolean =>
  window.__afterpayMobileUserJourneyV1 === true && typeof window.AfterpayReactNativeWebView?.postMessage === 'function'

export const MobileUserJourneyOwnership: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const featureFlagsReady = useLaunchDarklyIdentityReady()
  const enabled = useReadyFeatureFlag<boolean>(Features.MOBILE_USER_JOURNEY_OWNERSHIP, false)
  const reportingEnabled = useReadyFeatureFlag<boolean>(Features.USER_JOURNEY_INSTRUMENTATION, false)
  const mobileCapable = useRef(typeof window !== 'undefined' && hasMobileOwnershipCapability()).current
  const initialOwner = mobileCapable ? 'negotiating' : 'rocketship'
  const [owner, setOwner] = useState<CheckoutJourneyOwner>(initialOwner)
  const [ownershipExitTimedOut, setOwnershipExitTimedOut] = useState(false)
  const [reportingEligibilityTimedOut, setReportingEligibilityTimedOut] = useState(false)
  const ownerRef = useRef<CheckoutJourneyOwner>(initialOwner)
  const offerRef = useRef<OwnershipOffer | undefined>(undefined)
  const commitRef = useRef<OwnershipCommit | undefined>(undefined)
  const acknowledgedRef = useRef(false)
  const claimedByRocketshipRef = useRef(false)
  const negotiationEnabledRef = useRef(false)
  const fallbackFromNegotiationRef = useRef(false)
  const eligibilityWasPendingRef = useRef(mobileCapable && !featureFlagsReady)
  const eligibilityResolvedRef = useRef(!mobileCapable)
  const reportingEligibilityResolvedRef = useRef(featureFlagsReady)
  const reportingEligibilityTimedOutRef = useRef(false)
  const reportingEnabledRef = useRef(reportingEnabled)
  const reportingEligibilityWaitersRef = useRef<Array<() => void>>([])
  const ownershipResolutionWaitersRef = useRef<Array<() => void>>([])
  const ownershipExitTimeoutRef = useRef<number | undefined>(undefined)
  const ownershipExitTimedOutRef = useRef(false)
  const fallbackRef = useRef<number | undefined>(undefined)
  const reportingEligibilityResolved = featureFlagsReady || reportingEligibilityTimedOut
  const ownershipExitReady = owner !== 'negotiating' || ownershipExitTimedOut
  reportingEligibilityResolvedRef.current = reportingEligibilityResolved
  reportingEligibilityTimedOutRef.current = reportingEligibilityTimedOut
  reportingEnabledRef.current = reportingEnabled

  const clearFallback = useCallback((): void => {
    if (fallbackRef.current !== undefined) {
      window.clearTimeout(fallbackRef.current)
      fallbackRef.current = undefined
    }
  }, [])

  const resolveOwnershipWaiters = useCallback((): void => {
    if (ownershipExitTimeoutRef.current !== undefined) {
      window.clearTimeout(ownershipExitTimeoutRef.current)
      ownershipExitTimeoutRef.current = undefined
    }
    for (const resolve of ownershipResolutionWaitersRef.current.splice(0)) {
      resolve()
    }
  }, [])

  const resolveRocketshipOwnership = useCallback(
    (didFallback = negotiationEnabledRef.current): void => {
      fallbackFromNegotiationRef.current ||= didFallback
      clearFallback()
      negotiationEnabledRef.current = false
      ownerRef.current = 'rocketship'
      setOwner('rocketship')
      resolveOwnershipWaiters()
    },
    [clearFallback, resolveOwnershipWaiters]
  )

  const armFallback = useCallback((): void => {
    clearFallback()
    fallbackRef.current = window.setTimeout(() => resolveRocketshipOwnership(true), MOBILE_OWNERSHIP_READY_TIMEOUT_MS)
  }, [clearFallback, resolveRocketshipOwnership])

  const claimRocketshipOwnership = useCallback((): CheckoutJourneyStartBoundary | undefined => {
    if (
      !reportingEligibilityResolvedRef.current ||
      ownerRef.current === 'afterpay-mobile' ||
      (ownerRef.current === 'negotiating' && acknowledgedRef.current) ||
      claimedByRocketshipRef.current
    ) {
      return undefined
    }

    if (!reportingEnabledRef.current) {
      if (reportingEligibilityTimedOutRef.current) {
        claimedByRocketshipRef.current = true
        if (ownerRef.current === 'negotiating') {
          resolveRocketshipOwnership()
        }
      }
      return undefined
    }

    if (!mobileCapable) return undefined

    claimedByRocketshipRef.current = true
    if (ownerRef.current === 'negotiating') {
      resolveRocketshipOwnership()
    }
    const offer = offerRef.current
    return offer ? { journeyId: offer.checkoutFlowToken, startedAtEpochMs: offer.payload.startedAtEpochMs } : {}
  }, [mobileCapable, resolveRocketshipOwnership])
  const canStartRocketshipJourney = useCallback((): boolean => !claimedByRocketshipRef.current, [])
  const isRocketshipOwner = useCallback((): boolean => ownerRef.current === 'rocketship', [])
  const waitForReportingEligibility = useCallback((): Promise<void> => {
    if (reportingEligibilityResolvedRef.current) return Promise.resolve()
    return new Promise((resolve) => reportingEligibilityWaitersRef.current.push(resolve))
  }, [])
  const waitForOwnershipResolution = useCallback((): Promise<void> => {
    if (ownerRef.current !== 'negotiating' || ownershipExitTimedOutRef.current) return Promise.resolve()
    if (ownershipExitTimeoutRef.current === undefined) {
      ownershipExitTimeoutRef.current = window.setTimeout(() => {
        ownershipExitTimedOutRef.current = true
        claimedByRocketshipRef.current = true
        setOwnershipExitTimedOut(true)
        resolveOwnershipWaiters()
      }, MOBILE_OWNERSHIP_READY_TIMEOUT_MS)
    }
    return new Promise((resolve) => ownershipResolutionWaitersRef.current.push(resolve))
  }, [resolveOwnershipWaiters])

  const post = useCallback((message: unknown): boolean => {
    try {
      const bridge = window.AfterpayReactNativeWebView
      if (typeof bridge?.postMessage !== 'function') return false
      bridge.postMessage(JSON.stringify(message), window.location.origin)
      return true
    } catch {
      return false
    }
  }, [])

  useEffect(() => {
    if (!mobileCapable) return undefined

    const onMessage = (event: MessageEvent): void => {
      if (!negotiationEnabledRef.current || event.origin !== window.location.origin || event.source !== window) return
      const { data } = event
      const command = parseOwnershipCommand(data)
      if (command === undefined) return

      if (command.type === 'ownership_offer') {
        if (ownerRef.current === 'rocketship') return
        if (offerRef.current !== undefined && offerRef.current.checkoutFlowToken !== command.checkoutFlowToken) return
        offerRef.current = command
        if (post(createOwnershipResponse('ownership_ack', command.checkoutFlowToken)) && !acknowledgedRef.current) {
          acknowledgedRef.current = true
          clearFallback()
        }
        return
      }

      if (offerRef.current?.checkoutFlowToken !== command.checkoutFlowToken) return
      if (ownerRef.current === 'rocketship') return

      if (command.type === 'ownership_release') {
        if (ownerRef.current !== 'negotiating' || !acknowledgedRef.current || ownershipExitTimedOutRef.current) return
        acknowledgedRef.current = false
        resolveRocketshipOwnership(true)
        return
      }

      if (commitRef.current !== undefined && commitRef.current.payload.journeyId !== command.payload.journeyId) return
      commitRef.current = command
      clearFallback()
      ownerRef.current = 'afterpay-mobile'
      setOwner('afterpay-mobile')
      resolveOwnershipWaiters()
      post(createOwnershipResponse('ownership_committed', command.checkoutFlowToken, command.payload.journeyId))
    }

    window.addEventListener('message', onMessage)
    return () => {
      clearFallback()
      if (ownershipExitTimeoutRef.current !== undefined) {
        window.clearTimeout(ownershipExitTimeoutRef.current)
        ownershipExitTimeoutRef.current = undefined
      }
      window.removeEventListener('message', onMessage)
    }
  }, [clearFallback, mobileCapable, post, resolveOwnershipWaiters, resolveRocketshipOwnership])

  useEffect(() => {
    if (!mobileCapable || !featureFlagsReady || eligibilityResolvedRef.current || ownerRef.current !== 'negotiating') return

    eligibilityResolvedRef.current = true
    if (!enabled) {
      resolveRocketshipOwnership(eligibilityWasPendingRef.current)
      return
    }

    negotiationEnabledRef.current = true
    armFallback()
    if (!post(createOwnershipReady())) {
      resolveRocketshipOwnership()
    }
  }, [armFallback, enabled, featureFlagsReady, mobileCapable, post, resolveRocketshipOwnership])

  useEffect(() => {
    if (featureFlagsReady) {
      setReportingEligibilityTimedOut(false)
      return undefined
    }
    const timeout = window.setTimeout(() => setReportingEligibilityTimedOut(true), MOBILE_OWNERSHIP_READY_TIMEOUT_MS)
    return () => window.clearTimeout(timeout)
  }, [featureFlagsReady])

  useEffect(() => {
    if (!reportingEligibilityResolved) return
    for (const resolve of reportingEligibilityWaitersRef.current.splice(0)) {
      resolve()
    }
  }, [reportingEligibilityResolved])

  const value = useMemo(() => {
    const offer = offerRef.current
    return {
      owner,
      didFallbackFromMobileNegotiation: fallbackFromNegotiationRef.current,
      ownershipExitReady,
      reportingEligibilityResolved,
      reportingEnabled,
      ...(offer === undefined
        ? {}
        : { journeyId: commitRef.current?.payload.journeyId ?? offer.checkoutFlowToken, startedAtEpochMs: offer.payload.startedAtEpochMs }),
      claimRocketshipOwnership,
      canStartRocketshipJourney,
      isRocketshipOwner,
      waitForReportingEligibility,
      waitForOwnershipResolution,
    }
  }, [
    canStartRocketshipJourney,
    claimRocketshipOwnership,
    isRocketshipOwner,
    owner,
    ownershipExitReady,
    reportingEligibilityResolved,
    reportingEnabled,
    waitForOwnershipResolution,
    waitForReportingEligibility,
  ])
  return <CheckoutJourneyOwnershipContext.Provider value={value}>{children}</CheckoutJourneyOwnershipContext.Provider>
}

export const useCheckoutJourneyOwner = (): CheckoutJourneyOwner => useContext(CheckoutJourneyOwnershipContext).owner
export const useCheckoutJourneyOwnership = (): CheckoutJourneyOwnership => useContext(CheckoutJourneyOwnershipContext)
